import React, {useCallback, useEffect, useMemo, useState} from "react"
import {Claims, useAccessToken, useIsBankUser, useIsOrgUser, useIsUnitUser, useUserType} from "../../services/auth"
import {usePaging} from "../../hooks/usePaging"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {startCase, upperFirst} from "lodash"
import {ErrorDocument} from "../../resources/common"
import {useQueryState} from "use-location-state"
import {Filter} from "../Filter/Filter"
import {
	findPayments,
	PaymentDirection,
	PaymentFeature,
	PaymentStatus,
	PaymentStatusPhase,
	PaymentType,
	uploadBulkWires,
} from "../../resources/payment"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AsyncResult} from "../../types/asyncResult"
import moment from "moment"
import classNames from "classnames"
;("./ClearAchPaymentHelper")
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {useToasts} from "react-toast-notifications"
import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import DatePickerWithPresets, {DatePickerPresetKeys} from "../DatePicker/DatePicker"
import {Cents} from "../../resources/transaction"
import {CurrencyRangeFilter} from "../Filter/CurrencyRangeFilter"
import {downloadFile} from "../../utilities/file"
import {Bank, findBanks} from "../../resources/bank"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faCircleNotch, faUpload} from "@fortawesome/free-solid-svg-icons"
import {UnitAdminOnly} from "../../containers/PermissionedUser/PermissionedUser"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {findOrgs} from "../../resources/org"
import {Org} from "../../resources/org"
import {AllowedPaymentColumns} from "./Payments.types"
import {PaymentsTable} from "./PaymentsTable"

interface PaymentsProps {
	accountId?: string
	token?: string
	limit?: number
	includedColumns: AllowedPaymentColumns[]
	enableTitle?: boolean
	fullHeight?: boolean
	showTabsOnHeader?: boolean
	enableDirectionFilter?: boolean
	enableOrgFilter?: boolean
	enableBankFilter?: boolean
	enableStatusFilter?: boolean
	enableTypeFilter?: boolean
	enableStatusPhasesFilter?: boolean
	enableAmountFilter?: boolean
	enableFeaturesFilter?: boolean
	enableDateFilter?: boolean
	disableDrawer?: boolean
	enablePaging?: boolean
	paymentTypes?: PaymentType[]
	enableExport?: boolean
	title?: string
	claims?: Claims
	className?: string
	titleClassName?: string
	hideMinHeight?: boolean
	disabled?: boolean
	showUploadWireFileButton?: boolean
}

export default function Payments({
	limit = 25,
	token,
	includedColumns,
	fullHeight,
	showTabsOnHeader,
	enableTitle,
	accountId,
	paymentTypes,
	enableDirectionFilter = false,
	enableOrgFilter = false,
	enableBankFilter = false,
	enableStatusFilter = false,
	enableTypeFilter = false,
	enableStatusPhasesFilter = false,
	enableAmountFilter = false,
	enableFeaturesFilter = false,
	disableDrawer = false,
	enableDateFilter = true,
	enablePaging = true,
	enableExport = true,
	title = "Payments",
	claims,
	className,
	titleClassName,
	hideMinHeight,
	disabled,
	showUploadWireFileButton = true,
}: PaymentsProps) {
	const prefix = "paym"
	const accessToken = token ?? useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()
	const userType = useUserType(claims)
	const isOrgToken = useIsOrgUser(claims)
	const {addToast} = useToasts()
	const [since, setSince] = useQueryState(`${prefix}-filter[since]`, "")
	const [until, setUntil] = useQueryState(`${prefix}-filter[until]`, "")
	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>(`${prefix}-filter[orgs]`, [])
	const [filteredBanks, setFilteredBanks] = useQueryState<string[]>(`${prefix}-filter[banks]`, [])
	const statusesOptions = new Map(
		[
			PaymentStatus.Pending,
			PaymentStatus.PendingReview,
			PaymentStatus.Rejected,
			PaymentStatus.Clearing,
			PaymentStatus.Sent,
			PaymentStatus.Canceled,
			PaymentStatus.Returned,
		].map((v) => [v, startCase(v)])
	)
	const statusPhasesOptions = new Map(
		[
			PaymentStatusPhase.AwaitingProcessing,
			PaymentStatusPhase.WaitingForNotice,
			PaymentStatusPhase.WaitingForAcknowledgment,
			PaymentStatusPhase.Acknowledged,
		].map((v) => [v, startCase(v)])
	)
	const directionsOptions = new Map([PaymentDirection.Credit, PaymentDirection.Debit].map((v) => [v, v]))
	const typesOptions = new Map(
		Object.entries(PaymentType)
			.map((t) => [upperFirst(t[1]), startCase(t[0])] as [string, string])
			.sort((a, b) => a[1].localeCompare(b[1]))
	)
	const featuresOptions = new Map(
		[
			PaymentFeature.RecurringPayment,
			PaymentFeature.SameDay,
			PaymentFeature.ClearingDaysOverride,
			PaymentFeature.WireDrawdown,
		].map((v) => [v, startCase(v)])
	)
	const [statuses, setStatuses] = useQueryState(
		`${prefix}-filter[status]`,
		enableStatusFilter ? [isOrgToken ? PaymentStatus.Pending : PaymentStatus.PendingReview] : []
	)
	const paymentTypesToFilter = paymentTypes?.map((t) => upperFirst(t))
	const [types, setTypes] = useQueryState<string[]>(
		`${prefix}-filter[type]`,
		paymentTypesToFilter ? paymentTypesToFilter : []
	)
	const [statusPhases, setStatusPhases] = useQueryState<string[]>(`${prefix}-filter[statusPhase]`, [])
	const [directions, setDirections] = useQueryState<string[]>(`${prefix}-filter[direction]`, [])
	const [features, setFeatures] = useQueryState<string[]>(`${prefix}-filter[feature]`, [])
	const [fromAmount, setFromAmount] = useQueryState<Cents | "">(`${prefix}-filter[fromAmount]`, "")
	const [toAmount, setToAmount] = useQueryState<Cents | "">(`${prefix}-filter[toAmount]`, "")
	const [sortBy, setSortBy] = useQueryState(`${prefix}-sort`, "-createdAt")
	const [selectedPaymentId, setSelectedPaymentId] = useQueryState("paymentId", "")

	const escFunction = useCallback((event: KeyboardEvent) => {
		if (event.key === "Escape") {
			setSelectedPaymentId("")
		}
		return null
	}, [])

	useEffect(() => {
		document.addEventListener("keydown", escFunction, false)
	}, [])

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) =>
			findPayments(
				accessToken,
				offset,
				sortBy,
				"json",
				limit,
				statuses,
				statusPhases,
				filteredOrgs,
				filteredBanks,
				types,
				directions,
				features,
				since,
				until,
				accountId,
				fromAmount,
				toAmount
			),
		(x) => x.data.length,
		[
			refreshToken,
			since,
			until,
			sortBy,
			[...statuses, ...statusPhases, ...types, ...filteredOrgs, ...directions, ...features, ...filteredBanks].join(","),
			fromAmount,
			toAmount,
		],
		`${prefix}-`,
		true
	)

	const orgs =
		(userType === "unit" || userType === "bank") && enableOrgFilter
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredOrgs, filteredOrgs, reset, limit),
		[orgs, setFilteredOrgs, filteredOrgs.join(","), reset, limit]
	)

	const banks = useIsUnitUser(claims)
		? useAsyncResult(() => findBanks(accessToken, 0, 10000), [])
		: AsyncResult.pending<Bank[], ErrorDocument>()
	const bankFilter = banks.match(
		() => null,
		(banks) => (
			<Filter
				title="Banks"
				isSearchable={true}
				setStatuses={setFilteredBanks}
				statuses={filteredBanks}
				onFilterFunc={() => reset(limit)}
				options={
					new Map<string, string>(
						banks
							.sort((a, b) => moment(b.attributes.createdAt).diff(moment(a.attributes.createdAt)))
							.map((bank) => [bank.id, bank.attributes.name])
					)
				}
			/>
		),
		(_) => null
	)

	const statusFilter = (
		<Filter
			statuses={statuses}
			setStatuses={setStatuses}
			onFilterFunc={() => reset(limit)}
			options={statusesOptions}
			title="Status"
		/>
	)

	const dateTime = (
		<DatePickerWithPresets
			calendarPosition={"right"}
			presets={[
				DatePickerPresetKeys.allTime,
				DatePickerPresetKeys.yesterday,
				DatePickerPresetKeys.lastMonth,
				DatePickerPresetKeys.last3Months,
				DatePickerPresetKeys.last6Months,
				DatePickerPresetKeys.last30Days,
				DatePickerPresetKeys.last7Days,
				DatePickerPresetKeys.custom,
			]}
			onDateChanged={(s, u) => {
				setSince(s)
				setUntil(u)
			}}
		/>
	)

	const directionFilter = (
		<Filter
			statuses={directions}
			setStatuses={setDirections}
			onFilterFunc={() => reset(limit)}
			options={directionsOptions}
			title="Direction"
		/>
	)

	const statusPhasesFilter = (
		<Filter
			statuses={statusPhases}
			setStatuses={setStatusPhases}
			onFilterFunc={() => reset(limit)}
			options={statusPhasesOptions}
			title="&nbsp;Status Phase &nbsp;"
		/>
	)

	const typesFilter = (
		<Filter
			statuses={types}
			setStatuses={setTypes}
			onFilterFunc={() => reset(limit)}
			options={typesOptions}
			title="Type"
		/>
	)

	const featuresFilter = (
		<Filter
			statuses={features}
			setStatuses={setFeatures}
			onFilterFunc={() => reset(limit)}
			options={featuresOptions}
			title="Feature"
		/>
	)

	const amountFilter = (
		<CurrencyRangeFilter
			placeholder={"Amount"}
			presets={[]}
			from={fromAmount}
			setFrom={setFromAmount}
			to={toAmount}
			setTo={setToAmount}
		/>
	)

	function toggleSort() {
		setSortBy(sortBy == "-createdAt" ? "createdAt" : "-createdAt")
		reset(limit)
	}

	async function exportPayments() {
		const response = await findPayments(
			accessToken,
			0,
			sortBy,
			"csv",
			null,
			statuses,
			statusPhases,
			filteredOrgs,
			filteredBanks,
			types,
			directions,
			features,
			since,
			until,
			accountId,
			fromAmount,
			toAmount
		)

		response.match(
			(d) => downloadFile("Payments.csv", d, "text/csv"),
			() => {
				addToast("Something went wrong", {appearance: "warning"})
			}
		)
		return true
	}

	const [isFileLoading, setIsFileLoading] = useState(false)

	function onFileUploaded(e: React.ChangeEvent<HTMLInputElement>) {
		const files = e.target.files
		if (files) {
			const file = files[0]

			if (file) {
				setIsFileLoading(true)
				uploadBulkWires(accessToken, file, "text/csv", file.name)
					.then((result) => {
						setIsFileLoading(false)
						result.match(
							() =>
								addToast(`Successfully uploaded wires, summary will be sent by email.`, {
									appearance: "success",
								}),
							(errorResult) => {
								addToast(`${errorResult.errors[0].title} - ${errorResult.errors[0].detail}`, {appearance: "error"})
							}
						)
						refresh()
					})
					.catch(() => setIsFileLoading(false))
			}
		}
	}

	return (
		<DataTableCard className={"payments-card"} cardClassName={className}>
			{showUploadWireFileButton && (
				<UnitAdminOnly claims={claims}>
					<div className="file column is-right is-flex is-align-items-right">
						<a className="is-flex is-align-items-right ml-1" data-tip="React-tooltip">
							<label className="file-label">
								<input
									className="file-input"
									disabled={isFileLoading}
									type="file"
									onChange={onFileUploaded}
									accept=".text,.csv"
								/>
								<span className={classNames("file-cta", isFileLoading && "file-input-disabled")}>
									<span className="file-icon">
										<FontAwesomeIcon icon={isFileLoading ? faCircleNotch : faUpload} spin={isFileLoading} />
									</span>
									<span className="file-label">Upload Manual Wires CSV file</span>
								</span>
							</label>
						</a>
					</div>
				</UnitAdminOnly>
			)}

			<DataTableActionHeader
				enableSticky={fullHeight}
				dateTimePicker={enableDateFilter ? dateTime : null}
				filters={[
					...(enableOrgFilter ? [orgFilter] : []),
					...(enableBankFilter ? [bankFilter] : []),
					...(enableStatusFilter ? [statusFilter] : []),
					...(enableDirectionFilter ? [directionFilter] : []),
					...(enableAmountFilter ? [amountFilter] : []),
					...(enableStatusPhasesFilter && (useIsUnitUser() || useIsBankUser()) ? [statusPhasesFilter] : []),
					...(enableTypeFilter ? [typesFilter] : []),
					...(enableFeaturesFilter ? [featuresFilter] : []),
				]}
				exportFunc={enableExport ? exportPayments : undefined}
				titleClassName={classNames(titleClassName, disabled && "disabled")}
				title={enableTitle ? title : null}
				className={classNames(showTabsOnHeader && "with-title-bar-tabs")}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: payments}) => {
					return (
						<PaymentsTable
							payments={payments.data}
							meta={payments.meta}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							refresh={refresh}
							isUsingPaging={enablePaging}
							include={payments.included}
							fullHeight={fullHeight}
							showTabsOnHeader={showTabsOnHeader}
							includedColumns={includedColumns}
							sortFunction={toggleSort}
							sortBy={sortBy}
							selectedPaymentId={selectedPaymentId}
							setSelectedPaymentId={setSelectedPaymentId}
							disableDrawer={disableDrawer}
							hideMinHeight={hideMinHeight}
							disabled={disabled}
							token={accessToken}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
